import { FC, Fragment, HTMLAttributes, ReactNode } from "react";
import Image from "next/image";
import Link from "next/link";
import clsx from "clsx";

import HtmlText from "@/sections/common/HtmlText";
import icon, { IIconName } from "@/utils/icon.helper";

// -------------------------------------------------------------------------------------------------------

export type ISocialHandle = {
  platform: IIconName;
  link?: string;
};

export type IAuthorDetails = {
  image?: { src: string; alt: string };
  name?: string;
  designation?: string;
  description?: ReactNode;
  socialHandles?: ISocialHandle[];
};

export type IBlogAuthorCardSectionProps = HTMLAttributes<HTMLElement> &
  IAuthorDetails & {
    /**
     * Stacked compact layout — avatar on top, name + designation centered,
     * description below. Designed for the blog detail right-rail aside
     * where horizontal space is ~240px.
     */
    compact?: boolean;
  };

const BlogAuthorCardSection: FC<IBlogAuthorCardSectionProps> = (props) => {
  const {
    image,
    name,
    designation,
    description,
    socialHandles,
    compact = false,
    className,
    ...other
  } = props;

  if (
    !name &&
    !image &&
    !designation &&
    !socialHandles?.every((el) => el.link !== undefined)
  )
    return null;

  // Compact stacked variant for narrow contexts (right-rail aside).
  if (compact) {
    return (
      <section
        className={clsx(
          "rounded-xl border border-black-100 bg-white p-5",
          className,
        )}
        {...other}
      >
        <p className="mb-4 font-bricolage-grotesque text-xs font-medium uppercase tracking-wider text-black-600">
          About the author
        </p>
        <div className="flex flex-col items-start gap-3">
          {image && (
            <Image
              className="size-14 rounded-full border border-black-200 object-cover object-top"
              src={image.src}
              alt={name ?? ""}
              width={56}
              height={56}
            />
          )}
          {(name || designation) && (
            <div>
              {name && (
                <span className="block font-bricolage-grotesque text-base font-medium leading-tight text-black-900">
                  {name}
                </span>
              )}
              {designation && (
                <span className="mt-0.5 block text-xs font-normal text-gray-500">
                  {designation}
                </span>
              )}
            </div>
          )}
        </div>

        {description && (
          <div className="mt-4 line-clamp-6 text-xs leading-relaxed text-black-600 [&_p]:m-0 [&_p+p]:mt-2">
            {description}
          </div>
        )}

        {socialHandles && socialHandles.length > 0 && (
          <div className="mt-4 flex items-center gap-3 border-t border-black-100 pt-4">
            {socialHandles.map(
              (item, idx) =>
                item.link && (
                  <Link key={idx} href={item.link} target="_blank">
                    <Image
                      src={icon(item.platform)}
                      alt={item.platform}
                      height={18}
                      width={18}
                      className="size-[18px]"
                    />
                  </Link>
                ),
            )}
          </div>
        )}
      </section>
    );
  }

  // Default wide variant — used inline below the article body.
  return (
    <section
      className={clsx(
        "pb-5 pt-10 lg:pb-8 lg:pt-16 xl:pb-[50px] xl:pt-[100px]",
        className,
      )}
      {...other}
    >
      <div className="bg-primary-50 p-6 lg:p-8 xl:p-12">
        <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-center">
          <div className="flex items-center gap-4 lg:gap-6">
            {image && (
              <Image
                className="box-border size-16 rounded-full border border-solid border-black-950 bg-primary-200 object-cover object-top drop-shadow-[0_4px_4px_rgb(0,0,0,0.25)] md:size-20 lg:size-[100px]"
                src={image.src}
                alt={name ?? ""}
                width={103}
                height={100}
              />
            )}

            {(name || designation) && (
              <div>
                {name && (
                  <span className="block text-lg font-normal text-black-900 lg:text-h6 xl:text-h5">
                    {name}
                  </span>
                )}

                {designation && (
                  <span className="text-sm font-normal text-gray-500 md:text-base">
                    {designation}
                  </span>
                )}
              </div>
            )}
          </div>

          {socialHandles && socialHandles.length > 0 && (
            <div className="flex items-center gap-3 md:gap-5">
              {socialHandles.map((item, idx) => (
                <Fragment key={idx}>
                  {item.link && (
                    <Link href={item.link} target="_blank">
                      <Image
                        src={icon(item.platform)}
                        alt={item.platform}
                        height={20}
                        width={20}
                        className="size-5"
                      />
                    </Link>
                  )}
                </Fragment>
              ))}
            </div>
          )}
        </div>

        {description && <HtmlText className="mt-5">{description}</HtmlText>}
      </div>
    </section>
  );
};

export default BlogAuthorCardSection;
